import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Particle from "../Particle";

import imgAIReport from "../../Assets/Projects/bcw/aireportgenerator.png";
import imgAIConfig from "../../Assets/Projects/bcw/aiconfig.png";

const ss = { borderRadius: "8px", width: "100%", marginBottom: "10px" };
const label = { color: "rgb(155 126 172)", marginBottom: "6px", marginTop: "14px" };

function Section({ title, children }) {
  return (
    <>
      <Row style={{ justifyContent: "center", paddingTop: "30px" }}>
        <Col md={10}>
          <h4 style={{ color: "white", borderBottom: "1px solid rgb(100 60 140)", paddingBottom: "8px" }}>
            {title}
          </h4>
        </Col>
      </Row>
      {children}
    </>
  );
}

function Shot({ src, caption, md = 10 }) {
  return (
    <Row style={{ justifyContent: "center", paddingBottom: "6px" }}>
      <Col md={md}>
        <p style={label}>{caption}</p>
        <img src={src} alt={caption} className="img-fluid" style={ss} />
      </Col>
    </Row>
  );
}

function AITooling() {
  return (
    <Container fluid className="project-section">
      <Particle />
      <Container>
        <h1 className="project-heading">
          <strong className="purple">AI Tooling</strong> & Agents
        </h1>
        <p style={{ color: "white", textAlign: "center" }}>
          Purpose-built AI agents designed around the workflows my team actually uses - not generic assistants, but tools wired directly into our systems to reduce overhead and surface better information faster.
        </p>

        <Row style={{ justifyContent: "center", paddingTop: "20px", paddingBottom: "10px", color: "white" }}>
          <Col md={10} className="project-card">
            <h4>Overview</h4>
            <br />
            <p style={{ color: "white", textAlign: "left" }}>
              Starting in early 2026, I began building AI agents using Microsoft Copilot Studio to solve specific, repeatable pain points in how our team manages projects and communicates status. Each agent targets a task that was either time-consuming to do manually or inconsistently done across the team.
            </p>
            <p style={{ color: "white", textAlign: "left" }}>
              These agents are integrated directly into BetterConnectWise, our internal project management interface, and are exposed via a clean UI - no prompt engineering required from the end user.
            </p>

            <h4>Agents Built</h4>
            <br />
            <p style={{ color: "white", textAlign: "left" }}>
              <ul>
                <li>
                  <b>Project Summary Writer</b> - Takes a project's recent activity feed and generates a concise, client-ready status summary. Eliminates the need to manually comb through notes before a call or update.
                </li>
                <li>
                  <b>Milestone Generator</b> - Analyzes a project's scope and work plan, then suggests a logical milestone structure. Useful for structuring new projects quickly without starting from scratch.
                </li>
                <li>
                  <b>Portfolio Briefing</b> - Generates a high-level overview of the full active project portfolio - health status, stale projects, upcoming deadlines. Built for Monday morning stand-ups.
                </li>
                <li>
                  <b>Activity Report Generator</b> - Pulls time entries and activity logs for a given date range and produces a formatted report, either for internal review or client-facing delivery. Supports AI-written summaries and shareable link output.
                </li>
              </ul>
            </p>

            <h4>Built With</h4>
            <br />
            <p style={{ color: "white", textAlign: "left" }}>
              <ul>
                <li>Microsoft Copilot Studio for agent design, prompt configuration, and flow logic</li>
                <li>ConnectWise REST API as the primary data source</li>
                <li>React frontend for UI integration within BetterConnectWise</li>
                <li>Azure for hosting and identity</li>
                <li>Audit logging on all AI calls - every request is tied to a user, module, and outcome</li>
              </ul>
            </p>

            <h4>Design Principles</h4>
            <br />
            <p style={{ color: "white", textAlign: "left" }}>
              Every agent was built around a specific ask from someone on the team, not a hypothetical use case. The goal was to reduce the number of steps between "I need to know X" and actually having X. Agents are configurable through an admin panel and can be swapped or updated without touching the frontend code.
            </p>
          </Col>
        </Row>

        <Section title="Activity Report Generator">
          <Shot src={imgAIReport} caption="AI report generator - date range selection, AI-written summary, and shareable link output" />
        </Section>

        <Section title="Agent Configuration">
          <Shot src={imgAIConfig} caption="Admin panel - Copilot Studio agent wiring for each AI feature, configurable without code changes" />
        </Section>

      </Container>
    </Container>
  );
}

export default AITooling;
